import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { REVIEW_SUMMARY, reviewSummaryFor } from "../data/reviews";

// Star rating / review-count badge shown next to the product title.
// There is exactly ONE source of review figures site-wide: REVIEW_SUMMARY in
// data/reviews.js, derived from the real reviews held in that file. Every
// visible count, every label and the reviewCount in Product schema all read
// from here, so the site can never display one number while publishing
// another to Google. No reviews → honest fallback: no invented numbers, just a
// clickable "See customer reviews" link (fabricated ratings breach the ACL).
//
// No argument → the store-wide summary. Pass a product key (or a list of keys,
// see `covers` in data/reviews.js) → the summary for just that product page.
export function getReviewSummary(productKeys) {
  const summary = productKeys ? reviewSummaryFor(productKeys) : REVIEW_SUMMARY;
  if (summary && Number(summary.count) > 0 && Number(summary.average) > 0) {
    return summary;
  }
  return null;
}

// "3 verified reviews" style label from a summary object. Appends "from <source>"
// only when the summary carries a single source it can honestly claim.
export function reviewCountLabel(summary) {
  if (!summary) return "";
  // "verified" is only claimed when every counted review is a verified
  // purchase — otherwise the count is still real, just not all verified.
  const kind = summary.allVerified ? "verified review" : "review";
  const base = `${summary.count} ${kind}${summary.count === 1 ? "" : "s"}`;
  return summary.source ? `${base} from ${summary.source}` : base;
}

export function StarRow({ rating = 5, size = 15 }) {
  return (
    <span className="st-star-row" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={i <= Math.round(rating) ? 0 : 1.6}
          fill={i <= Math.round(rating) ? "var(--accent)" : "transparent"}
          style={{ color: "var(--accent)" }}
        />
      ))}
    </span>
  );
}

// The whole badge is a link to the reviews page.
export default function RatingBadge({ to = "/reviews", testid = "rating-badge" }) {
  const summary = getReviewSummary();

  if (summary) {
    return (
      <Link to={to} className="st-rating-badge" data-testid={testid} aria-label={`Rated ${summary.average} out of 5 from ${summary.count} reviews — read customer reviews`}>
        <StarRow rating={summary.average} />
        <span>
          <strong>{summary.average}/5</strong> based on{" "}
          <span className="st-rating-count">{reviewCountLabel(summary)}</span>
        </span>
      </Link>
    );
  }

  // Placeholder state — no live star data yet. Still clickable so buyers can
  // read the genuine customer comments on the reviews page.
  return (
    <Link to={to} className="st-rating-badge" data-testid={testid}>
      <Star size={15} strokeWidth={0} fill="var(--accent)" style={{ color: "var(--accent)" }} />
      <span className="st-rating-count">See customer reviews</span>
    </Link>
  );
}
